import { BackButton } from "@/components/ui/BackButton";
import { useLoading } from "@/hooks/use-loading";
import { getCategories } from "@/services/public.service";
import { Button, Card, Flex, Typography, Grid, message, Spin } from "antd";
import { useEffect, useState } from "react";
import type { CategoryItem, CategoryKey } from "./types/public";
import { Loader2 } from "lucide-react";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface CategoryProps {
  onBack: () => void;
  onSelect: (categoryKey: CategoryKey) => void;
}

const renderCriteria = (text: string, boldText: string) => {
  if (!boldText) return text;

  const parts = text.split(boldText);

  return parts.map((part, index) => (
    <span key={index}>
      {part}
      {index < parts.length - 1 && <strong>{boldText}</strong>}
    </span>
  ));
};

export default function Category({ onBack, onSelect }: CategoryProps) {
  const screens = useBreakpoint();

  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const { loading, withLoading } = useLoading();

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await withLoading(() =>
          getCategories({ languageCode: "ms" }),
        );

        if (res.status) {
          setCategories(res.data.categoryLists);
        }
      } catch (error) {
        console.error(error);
        message.error("Gagal mendapatkan kategori pendaftaran.");
      }
    };
    void loadCategories();
  }, []);

  return (
    <>
      {loading && (
        <Spin
          fullscreen
          description="Memuatkan..."
          indicator={
            <Loader2 className="animate-spin" style={{ fontSize: 48 }} />
          }
        />
      )}

      <Title
        level={2}
        style={{
          marginTop: 16,
          marginBottom: 0,
          textAlign: "center",
          fontWeight: 700,
          fontSize: screens.sm ? "32px" : "24px",
          color: "var(--color-primary)",
        }}
      >
        Pendaftaran
      </Title>

      <div className="mt-12">
        <div className="relative sm:flex sm:items-center sm:justify-center">
          <div className="sm:absolute sm:left-0 sm:w-full">
            <BackButton onClick={onBack} />
          </div>

          <Title
            level={3}
            style={{
              margin: 0,
              color: "#272728",
              fontWeight: 700,
              fontSize: screens.sm ? 24 : 18,
              marginTop: screens.sm ? 0 : 16,
              textAlign: "center",
            }}
          >
            Pilih Kategori Pendaftaran
          </Title>
        </div>

        <div className="grid mt-8 sm:mt-12 grid-cols-1 md:grid-cols-2 gap-12 mx-auto">
          {categories.map((category) => (
            <Card
              key={category.categoryKey}
              hoverable
              title={category.name.toUpperCase()}
              styles={{
                header: {
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: screens.sm ? "24px" : "18px",
                  backgroundColor: "#F4F8FF",
                  color: "var(--color-primary)",
                  padding: 24,
                },
                body: {},
              }}
              className="shadow-xl"
            >
              <Flex vertical gap={8}>
                {category.criteriaList.map((criteria, index) => (
                  <div key={index}>
                    <Text style={{ fontWeight: 500 }}>
                      {renderCriteria(criteria, category.criteriaBoldText)}
                    </Text>

                    {index < category.criteriaList.length - 1 && (
                      <Text
                        style={{
                          display: "block",
                          marginTop: 8,
                          color: "#666",
                        }}
                      >
                        atau
                      </Text>
                    )}
                  </div>
                ))}
              </Flex>

              <Button
                htmlType="button"
                size="large"
                style={{ marginTop: "40px", width: "100%" }}
                variant="solid"
                onClick={() => onSelect(category.categoryKey)}
              >
                Daftar sebagai {category.name}
              </Button>
            </Card>
          ))}
        </div>

        <div className="mt-8 sm:mt-12">
          <Paragraph
            style={{
              color: "#D7F4FF",
              textAlign: "center",
              alignSelf: "stretch",
              fontWeight: 500,
              fontSize: 14,
              textShadow: "0 0 4px rgba(0, 21, 43, 0.90)",
            }}
          >
            Sebarang pertanyaan berkenaan pendaftaran, boleh hubungi pejabat
            Lembaga Pembangunan Tioman (TDA) di talian 019-9283179
          </Paragraph>
        </div>
      </div>
    </>
  );
}
